import type { DisplayCoordinatorDelegate, PromptDisplayCoordinator } from '../types/display-coordinator.ts'
import type { Prompt, PromptGroup } from '../types/prompts.ts'
import { type Logger, TaggedLogger } from '@core/logging/logger.ts'
import { lazy } from '@core/decorators/lazy.ts'
import { Env } from '../env.ts'
import { PushSdkConfig, PushSdkCustomClassNames } from '../types/push-sdk-config.ts'
import type { UserStore } from '../storage/user-storage-manager.ts'
import { SubscriberStatus } from '../enums/subscriber-status.ts'
import { ensureError } from '@core/utils/ensure-error.ts'
import { EventTracker } from '@core/events/event-manager.ts'
import type { PromptDispatchMessage } from '../types/prompt-dispatch-message.ts'
import { PromptActionType } from '../types/prompt-action-type.ts'
import { ProviderPermissions } from '../permissions/notification-permission-service.ts'
import type { PromptDisplayOutcome } from '../types/display-coordinator.ts'

type CustomPromptElementsContainer = {
    element: Element
    actions: {
        allow: Element | null
        dismiss: Element | null
    }
}

// DEFAULT CLASSNAMES

const DEFAULT_CLASSNAME_WEB_PUSH_UNSUPPORTED = 'pushly-web-push-unsupported'
const DEFAULT_CLASSNAME_WEB_PUSH_SUPPORTED = 'pushly-web-push-supported'
const DEFAULT_CLASSNAME_SUBSCRIPTION_NONE = 'pushly-subscription-none'
const DEFAULT_CLASSNAME_SUBSCRIPTION_TRUE = 'pushly-subscription-subscribed'
const DEFAULT_CLASSNAME_SUBSCRIPTION_FALSE = 'pushly-subscription-unsubscribed'
const DEFAULT_CLASSNAME_SUBSCRIPTION_DENIED = 'pushly-subscription-denied'
const DEFAULT_CLASSNAME_SUBSCRIPTION_DISMISSED = 'pushly-subscription-dismissed'
const DEFAULT_CLASSNAME_PROMPT_INELIGIBLE = 'pushly-prompt-ineligible'
const DEFAULT_CLASSNAME_PROMPT_ELIGIBLE = 'pushly-prompt-eligible'

const DEFAULT_CLASSNAME_PROMPT_CUSTOM = 'pushly-prompt-custom'
const DEFAULT_CLASSNAME_PROMPT_VISIBLE = 'pushly-prompt-visible'
const DEFAULT_CLASSNAME_BTN_ALLOW = 'pushly-prompt-buttons-allow'
const DEFAULT_CLASSNAME_BTN_DISMISS = 'pushly-prompt-buttons-dismiss'

export class CustomPromptView implements PromptDisplayCoordinator {
    /**
     * Reference container for associated HTMLElements.
     */
    protected _promptElements: CustomPromptElementsContainer | null = null

    private readonly logger: Logger

    public constructor(
        public readonly prompt: Prompt,
        public readonly group: PromptGroup,
        protected readonly isEligible: boolean, // @todo - rethink name
        protected readonly pushSdkConfig: PushSdkConfig,
        protected readonly userStore: UserStore,
        protected readonly eventManager: EventTracker,
        protected readonly delegate: DisplayCoordinatorDelegate & {
            _processDispatchMessage(message: PromptDispatchMessage): Promise<void>
        },
        logger?: Logger,
    ) {
        this.logger = logger ?? TaggedLogger._getInstance('CustomPromptView')
        this._locateAndConfigure()
    }

    @lazy protected get _document(): Document {
        return Env._document
    }

    @lazy protected get _defaultElementStub(): HTMLElement {
        return this._document.createElement('div')
    }

    /**
     * Returns a proxied {@link CustomPromptElementsContainer} with
     * a guaranteed root element.
     */
    @lazy protected get _promptConfig(): CustomPromptElementsContainer {
        let warningLogged = false
        const logElementWarning = () => {
            if (warningLogged) {
                return
            }

            warningLogged = true
            this.logger.warn('Custom prompt element has not been configured yet')

            setTimeout(() => {
                warningLogged = false
            }, 500)
        }

        const emptyConfig = {
            actions: {
                allow: null,
                dismiss: null,
            },
        } as CustomPromptElementsContainer

        Object.defineProperty(emptyConfig, 'element', {
            get: () => {
                if (this._promptElements?.element) {
                    return this._promptElements.element
                }

                logElementWarning()
                return this._defaultElementStub
            },
        })

        return emptyConfig
    }

    /**
     * Attempts to locate the custom prompt in the current
     * documents HTML on an interval (500ms) until a match is found.
     *
     * Once found attempts to locate the actions buttons and wire
     * the click behaviors. Also sets initial classNames.
     */
    public _locateAndConfigure(): void {
        let timer: any = null

        const locateAndConfigure = async () => {
            try {
                clearTimeout(timer)

                const matches = this._document.getElementsByClassName(DEFAULT_CLASSNAME_PROMPT_CUSTOM)
                if (!matches.length) {
                    timer = setTimeout(locateAndConfigure, 500)
                    return
                }

                const element = matches[0]
                const config: CustomPromptElementsContainer = {
                    element: element,
                    actions: {
                        allow: element.getElementsByClassName(DEFAULT_CLASSNAME_BTN_ALLOW)[0] ?? null,
                        dismiss: element.getElementsByClassName(DEFAULT_CLASSNAME_BTN_DISMISS)[0] ?? null,
                    },
                }

                if (config.actions.allow) {
                    config.actions.allow.addEventListener('click', this._allow.bind(this))
                } else {
                    this.logger.warn('Prompt allow button cannot be located')
                }

                if (config.actions.dismiss) {
                    config.actions.dismiss.addEventListener('click', this._dismiss.bind(this))
                } else {
                    this.logger.warn('Prompt dismiss button cannot be located')
                }

                this._promptElements = config
                this.logger.info('Custom prompt located and configured')

                this._setSupportedClassName()
                await this._setInitialSubscriptionClassName()

                if (this.isEligible) {
                    this._setEligibleClassNames()
                } else {
                    this._setIneligibleClassNames()
                }
            } catch (err) {
                const error = ensureError(err)
                this.logger.warn(error)
                this.eventManager._track(error)
            }
        }

        locateAndConfigure().then()
    }

    /**
     * Returns a composite set of {@link PushSdkCustomClassNames} with
     * customized, if passed, or default values.
     */
    @lazy private get _classNames(): Required<PushSdkCustomClassNames> {
        const classNames = this.pushSdkConfig.classNames || {}

        classNames.webPushUnsupported = classNames.webPushUnsupported || DEFAULT_CLASSNAME_WEB_PUSH_UNSUPPORTED
        classNames.webPushSupported = classNames.webPushSupported || DEFAULT_CLASSNAME_WEB_PUSH_SUPPORTED
        classNames.subscriptionNone = classNames.subscriptionNone || DEFAULT_CLASSNAME_SUBSCRIPTION_NONE
        classNames.subscriptionSubscribed = classNames.subscriptionSubscribed || DEFAULT_CLASSNAME_SUBSCRIPTION_TRUE
        classNames.subscriptionUnsubscribed =
            classNames.subscriptionUnsubscribed || DEFAULT_CLASSNAME_SUBSCRIPTION_FALSE
        classNames.subscriptionDenied = classNames.subscriptionDenied || DEFAULT_CLASSNAME_SUBSCRIPTION_DENIED
        classNames.subscriptionDismissed = classNames.subscriptionDismissed || DEFAULT_CLASSNAME_SUBSCRIPTION_DISMISSED
        classNames.promptIneligible = classNames.promptIneligible || DEFAULT_CLASSNAME_PROMPT_INELIGIBLE
        classNames.promptEligible = classNames.promptEligible || DEFAULT_CLASSNAME_PROMPT_ELIGIBLE

        return classNames as Required<PushSdkCustomClassNames>
    }

    /**
     * Always succeeds: a Publisher Custom prompt's markup belongs to the publisher and is already
     * on the page, so there is nothing here that can fail to render.
     */
    public _display(): PromptDisplayOutcome {
        this.delegate._onPromptDidShow(this.prompt)

        return 'displayed'
    }

    public _hide(): void {
        this.delegate._onPromptDidHide(this.prompt)
    }

    /**
     * Sets the appropriate classNames based on response.
     */
    public _handlePermissionsResponse({ permission }: ProviderPermissions): void {
        switch (permission) {
            case 'granted':
                this._setPromptCompositeSubscribedClassNames()
                break

            case 'denied':
                this._setSubscriptionDeniedClassNames()
                break

            case 'default':
                this._setSubscriptionDismissedClassNames()
                break
        }
    }

    /**
     * Dispatches standard **allow** {@link PromptDispatchMessage}
     * with `is_custom_prompt: true`.
     */
    protected async _allow() {
        await this.delegate._processDispatchMessage({
            prompt_action: {
                id: this.prompt.id,
                type: PromptActionType.ALLOW,
                is_custom_prompt: true,
            },
        })
    }

    /**
     * Dispatches standard **dismiss** {@link PromptDispatchMessage}
     * with `is_custom_prompt: true`.
     */
    protected async _dismiss() {
        this._setSubscriptionDismissedClassNames()
        await this.delegate._processDispatchMessage({
            prompt_action: {
                id: this.prompt.id,
                type: PromptActionType.DISMISS,
                is_custom_prompt: true,
            },
        })
    }

    /**
     * Sets the appropriate classNames based on current subscription status.
     */
    protected async _setInitialSubscriptionClassName(): Promise<void> {
        const subscriberStatus = await this.userStore._getSubscriberStatus()
        if (subscriberStatus === SubscriberStatus.DENIED) {
            this._setDeniedClassNames()
        } else if (subscriberStatus === SubscriberStatus.DISMISSED) {
            this._setDismissedClassNames()
        } else if (subscriberStatus === SubscriberStatus.SUBSCRIBED) {
            this._setSubscribedClassNames()
        } else {
            this._setNoSubscriptionClassNames()
        }
    }

    /**
     * Sets the appropriate classNames and removes visible state.
     */
    protected _setPromptCompositeSubscribedClassNames(): void {
        this._setIneligibleClassNames()
        this._setSubscribedClassNames()

        this._promptConfig.element.classList.remove(DEFAULT_CLASSNAME_PROMPT_VISIBLE)
    }

    /**
     * Sets the appropriate classNames and removes visible state.
     */
    protected _setSubscriptionDismissedClassNames(): void {
        this._setIneligibleClassNames()
        this._setDismissedClassNames()

        this._promptConfig.element.classList.remove(DEFAULT_CLASSNAME_PROMPT_VISIBLE)
    }

    /**
     * Sets the appropriate classNames and removes visible state.
     */
    protected _setSubscriptionDeniedClassNames(): void {
        this._setIneligibleClassNames()
        this._setDeniedClassNames()

        this._promptConfig.element.classList.remove(DEFAULT_CLASSNAME_PROMPT_VISIBLE)
    }

    public _setUnsupportedClassName(): void {
        const { element } = this._promptConfig
        element.classList.add(this._classNames.webPushUnsupported)
        element.classList.remove(this._classNames.webPushSupported)
    }

    public _setSupportedClassName(): void {
        const { element } = this._promptConfig
        element.classList.add(this._classNames.webPushSupported)
        element.classList.remove(this._classNames.webPushUnsupported)
    }

    public _setEligibleClassNames(): void {
        const { element } = this._promptConfig
        element.classList.remove(this._classNames.promptIneligible)
        element.classList.add(this._classNames.promptEligible)
    }

    public _setIneligibleClassNames(): void {
        const { element } = this._promptConfig
        element.classList.remove(this._classNames.promptEligible)
        element.classList.add(this._classNames.promptIneligible)
    }

    protected _setNoSubscriptionClassNames(): void {
        const { element } = this._promptConfig
        element.classList.add(this._classNames.subscriptionNone)
        element.classList.remove(
            this._classNames.subscriptionSubscribed,
            this._classNames.subscriptionDismissed,
            this._classNames.subscriptionDenied,
            this._classNames.subscriptionUnsubscribed,
        )
    }

    protected _setSubscribedClassNames(): void {
        const { element } = this._promptConfig
        element.classList.add(this._classNames.subscriptionSubscribed)
        element.classList.remove(
            this._classNames.subscriptionNone,
            this._classNames.subscriptionDismissed,
            this._classNames.subscriptionDenied,
            this._classNames.subscriptionUnsubscribed,
        )
    }

    protected _setDismissedClassNames(): void {
        const { element } = this._promptConfig
        element.classList.add(this._classNames.subscriptionDismissed)
        element.classList.remove(
            this._classNames.subscriptionNone,
            this._classNames.subscriptionSubscribed,
            this._classNames.subscriptionDenied,
            this._classNames.subscriptionUnsubscribed,
        )
    }

    protected _setDeniedClassNames(): void {
        const { element } = this._promptConfig
        element.classList.add(this._classNames.subscriptionDenied)
        element.classList.remove(
            this._classNames.subscriptionNone,
            this._classNames.subscriptionSubscribed,
            this._classNames.subscriptionDismissed,
            this._classNames.subscriptionUnsubscribed,
        )
    }
}
